/**
 * Settings, including the call-target pool editor — the one bit of data entry
 * the product asks for. Every assignment comes from this list, so this is where
 * the honesty of the whole thing is maintained (SPEC §7.1).
 */
import { useCallback, useEffect, useState, type SyntheticEvent } from 'react';
import { api, ApiRequestError, type HealthResponse } from '../lib/api';
import { formatPhone } from '../lib/dial';
import {
  Button,
  Card,
  ErrorNote,
  Field,
  Label,
  Loading,
  Screen,
  inputClass,
} from '../components/ui';
import type { CallTarget, UserProfile } from '../../shared/types';

const CATEGORY_SUGGESTIONS = [
  'retail',
  'restaurant',
  'garden-center',
  'hardware',
  'grocery',
  'auto-shop',
  'landscaper',
  'contractor',
  'accountant',
  'insurance',
  'it-services',
  'agency',
  'peer-business',
  'prospect',
];

export function Settings({
  profile,
  onProfileChange,
  onSignOut,
}: {
  profile: UserProfile | null;
  onProfileChange: () => Promise<UserProfile>;
  onSignOut: () => void;
}) {
  const [targets, setTargets] = useState<CallTarget[] | null>(null);
  const [health, setHealth] = useState<HealthResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    businessName: '',
    phoneNumber: '',
    category: 'retail',
    location: '',
    notes: '',
  });

  const load = useCallback(async () => {
    try {
      const [loadedTargets, loadedHealth] = await Promise.all([api.targets(), api.health()]);
      setTargets(loadedTargets);
      setHealth(loadedHealth);
      setError(null);
    } catch (caught) {
      setError(caught instanceof ApiRequestError ? caught.message : 'Could not load settings.');
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function addTarget(event: SyntheticEvent) {
    event.preventDefault();
    setBusy(true);
    try {
      await api.createTarget({
        businessName: form.businessName.trim(),
        phoneNumber: form.phoneNumber.trim(),
        category: form.category.trim().toLowerCase(),
        location: form.location.trim() || undefined,
        notes: form.notes.trim() || undefined,
      });
      setForm({
        businessName: '',
        phoneNumber: '',
        category: form.category,
        location: '',
        notes: '',
      });
      await load();
    } catch (caught) {
      setError(caught instanceof ApiRequestError ? caught.message : 'Could not add that target.');
    } finally {
      setBusy(false);
    }
  }

  async function setTarget(count: number) {
    await api.updateProfile({ dailyCallTarget: count });
    await onProfileChange();
  }

  return (
    <Screen title="Settings">
      {error ? <ErrorNote>{error}</ErrorNote> : null}

      <Card className="flex flex-col gap-3">
        <Label>Calls per day</Label>
        <div className="flex gap-2">
          {[3, 5, 7, 10].map((option) => (
            <Button
              key={option}
              variant={profile?.dailyCallTarget === option ? 'primary' : 'secondary'}
              className="flex-1"
              onClick={() => void setTarget(option)}
            >
              {option}
            </Button>
          ))}
        </div>
        <p className="text-xs text-mist-400">Timezone: {profile?.timezone ?? '—'}</p>
      </Card>

      <Card className="flex flex-col gap-3">
        <Field
          label="Where to look for businesses"
          hint="Used by the target-finder agent, not by the app itself."
        >
          <input
            className={inputClass}
            defaultValue={profile?.searchLocation ?? ''}
            onBlur={(event) => {
              const value = event.target.value.trim();
              if (!value || value === profile?.searchLocation) return;
              void api.updateProfile({ searchLocation: value }).then(onProfileChange);
            }}
            placeholder="Duvall, WA"
          />
        </Field>
      </Card>

      <Card className="flex flex-col gap-2">
        <Label>Coach</Label>
        <p className="text-sm text-mist-200">
          {health?.aiConfigured
            ? 'Anthropic connected. Missions and observations are written by the model, difficulty is decided by the rules engine either way.'
            : 'No API key set. Running on the built-in rules coach — everything works, the prose is just plainer.'}
        </p>
      </Card>

      <Card className="flex flex-col gap-4">
        <div>
          <Label>Who you can call</Label>
          <p className="mt-1 text-xs text-mist-400">
            Assignments only ever use numbers from this list. Add real places you would genuinely
            ring — the app will never invent one.
          </p>
        </div>

        <form onSubmit={(event) => void addTarget(event)} className="flex flex-col gap-3">
          <Field label="Business">
            <input
              className={inputClass}
              value={form.businessName}
              onChange={(event) => {
                setForm({ ...form, businessName: event.target.value });
              }}
              placeholder="Duvall Garden Center"
              required
            />
          </Field>
          <Field label="Phone">
            <input
              className={inputClass}
              type="tel"
              value={form.phoneNumber}
              onChange={(event) => {
                setForm({ ...form, phoneNumber: event.target.value });
              }}
              placeholder="425 555 0134"
              required
            />
          </Field>
          <Field label="Category">
            <select
              className={inputClass}
              value={form.category}
              onChange={(event) => {
                setForm({ ...form, category: event.target.value });
              }}
            >
              {CATEGORY_SUGGESTIONS.map((category) => (
                <option key={category} value={category}>
                  {category}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Location" hint="Optional.">
            <input
              className={inputClass}
              value={form.location}
              onChange={(event) => {
                setForm({ ...form, location: event.target.value });
              }}
              placeholder="Duvall, WA"
            />
          </Field>
          <Button
            type="submit"
            variant="secondary"
            disabled={busy || !form.businessName.trim() || !form.phoneNumber.trim()}
          >
            {busy ? 'Adding…' : 'Add target'}
          </Button>
        </form>

        {targets === null ? (
          <Loading label="Loading targets" />
        ) : targets.length === 0 ? (
          <p className="text-sm text-mist-400">Nothing here yet.</p>
        ) : (
          <ul className="flex flex-col gap-2 border-t border-ink-700 pt-3">
            {targets.map((target) => (
              <li key={target.id} className="flex items-center justify-between gap-3 text-sm">
                <div className={target.active ? '' : 'opacity-50'}>
                  <p className="text-mist-200">{target.businessName}</p>
                  <p className="text-xs text-mist-400">
                    {formatPhone(target.phoneNumber)} · {target.category}
                    {target.timesCalled > 0 ? ` · called ${target.timesCalled}×` : ''}
                  </p>
                  {/* Provenance for anything an agent found, so a number that
                      looks wrong can be checked against where it came from. */}
                  {target.source === 'discovery' && target.sourceUrl ? (
                    <a
                      href={target.sourceUrl}
                      target="_blank"
                      rel="noreferrer noopener"
                      className="text-xs text-mist-400 underline decoration-ink-600"
                    >
                      found · check source
                    </a>
                  ) : null}
                </div>
                <div className="flex shrink-0 gap-1">
                  <Button
                    variant="ghost"
                    className="px-2 text-xs"
                    onClick={() => {
                      void api.updateTarget(target.id, { active: !target.active }).then(load);
                    }}
                  >
                    {target.active ? 'Pause' : 'Resume'}
                  </Button>
                  <Button
                    variant="ghost"
                    className="px-2 text-xs"
                    onClick={() => {
                      void api.deleteTarget(target.id).then(load);
                    }}
                  >
                    Delete
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </Card>

      <Card className="flex flex-col gap-3">
        <Label>Data</Label>
        <a
          href="/api/export"
          className="inline-flex min-h-[44px] items-center justify-center rounded-xl bg-ink-700 px-4 text-sm text-mist-200"
        >
          Export everything
        </a>
        <Button
          variant="danger"
          onClick={() => {
            if (
              !window.confirm('Wipe all reps, results, reviews and plans? Goals and targets stay.')
            )
              return;
            void api.reset().then(load);
          }}
        >
          Reset practice history
        </Button>
        <Button
          variant="ghost"
          onClick={() => {
            void api.logout().then(onSignOut);
          }}
        >
          Sign out
        </Button>
      </Card>
    </Screen>
  );
}
